import React from 'react';

/**
 * Bande « fenêtre de contexte » — quatre barres datées, échelle log.
 * Les valeurs viennent de `slide.figure.steps` : rien n'est calculé ici hormis
 * la hauteur des barres.
 */
const FigureBande = ({ figure, visible }) => {
    const maxi = Math.max(...figure.steps.map(s => s.pages));
    // Plafond a 84 % : les 16 % restants sont la place de l'etiquette de valeur,
    // qui est posee JUSTE AU-DESSUS de la barre et non en haut de la bande.
    // Sans ce plafond, la barre la plus haute chasse son etiquette hors cadre.
    //
    // Plancher a 0,6 % : la plus petite barre doit rester VISIBLE comme barre.
    // A 6 pages sur 1500 elle fait 0,34 % — quelques dixiemes de pixel, donc
    // rien du tout, et une colonne sans barre se lit comme une donnee manquante
    // et non comme une donnee minuscule. Le plancher ne ment pas : il epaissit
    // un trait deja invisible, l'etiquette porte la vraie valeur.
    const hauteur = pages => `${Math.max(0.6, (pages / maxi) * 84)}%`;

    return (
        <div
            className={`w-full max-w-5xl mx-auto mt-[2vh] flex flex-col flex-1 min-h-0 max-h-[40vh] transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
            {(figure.title || figure.unit) && (
                <div className="flex-shrink-0 flex items-baseline justify-center gap-3 mb-[1.4vh]">
                    {figure.title && <span className="text-blue-200/80 text-xl font-semibold">{figure.title}</span>}
                    {figure.unit && <span className="text-slate-500 text-base font-mono">{figure.unit}</span>}
                </div>
            )}

            <div className="flex items-end justify-between gap-6 flex-1 min-h-[8vh] px-2">
                {figure.steps.map((etape, i) => {
                    const dernier = i === figure.steps.length - 1;
                    return (
                        <div key={etape.year} className="flex-1 flex flex-col items-center h-full">
                            {/* La zone de barre prend tout le reste : c'est elle qui
                                porte le pourcentage, pas la colonne entiere — sinon
                                les etiquettes mangent la hauteur et les barres se
                                retrouvent toutes ecrasees a la meme taille. */}
                            <div className="w-full flex-1 min-h-0 flex flex-col justify-end items-center">
                                <span className={`font-mono text-base mb-1 whitespace-nowrap ${dernier ? 'text-green-300 font-bold' : 'text-slate-400'}`}>
                                    {etape.display}
                                </span>
                                <div
                                    className={`w-full rounded-t-md border-t border-x ${dernier
                                        ? 'bg-gradient-to-t from-green-500/10 to-green-400/50 border-green-400/60'
                                        : 'bg-gradient-to-t from-blue-500/5 to-blue-400/25 border-blue-400/30'
                                        }`}
                                    style={{ height: hauteur(etape.pages) }}
                                />
                            </div>
                            <span className="text-slate-200 font-mono text-lg mt-1">{etape.year}</span>
                            <span className="text-slate-500 font-mono text-sm leading-none">{etape.model}</span>
                        </div>
                    );
                })}
            </div>

            {figure.caption && (
                <p className="flex-shrink-0 text-center text-slate-200 text-2xl font-semibold mt-[1.6vh]">{figure.caption}</p>
            )}
        </div>
    );
};

/**
 * Template "comparaison" — deux colonnes, avant / après.
 *
 * ⚠️ LA HAUTEUR EST LA CONTRAINTE, PAS LA LARGEUR — même piège que
 * `StandardSlide`. Les cartes étaient posées à `min-h-[90px]` + `p-5` en pixels
 * FIXES, et les respirations (`mb-12`, `mb-6`, `space-y-4`) en rem, pendant que
 * la page de conférence met le rem racine à l'échelle de la LARGEUR. Sur une
 * fenêtre de 1080 moins le chrome du navigateur, la troisième carte de chaque
 * colonne mordait le bas de l'écran — et `justify-center` ne le signalait pas.
 *
 * Le rythme vertical est donc exprimé en `vh` : il se resserre sur un écran
 * court et s'ouvre sur un écran haut, au lieu d'être juste à une seule taille
 * d'écran. Le titre est borné par la hauteur autant que par sa taille nominale.
 * Ne pas y remettre de hauteur plancher en pixels : c'est ce qui a cassé.
 *
 * Une seule slide s'en sert (« De l'assistant à l'agent »), et son texte a
 * grossi le 08/09 — trois lignes par carte au lieu de deux. Si tu rallonges
 * encore les points, c'est ici que ça se verra en premier.
 *
 * `slide.figure` (optionnel) ajoute une bande de barres SOUS les deux colonnes.
 * Ses trois libellés — `title`, `unit`, `caption` — sont chacun facultatifs, et
 * la slide 4 les a TOUS RETIRÉS le 09/09 : les barres portent déjà leur valeur
 * en clair (« 6 pages », « 1 500 pages »), l'année et le modèle. Un titre
 * au-dessus d'un graphique qui se lit seul, c'est une ligne que la salle lit
 * au lieu d'écouter — et la chute, elle, est redevenue une phrase qui se dit.
 *
 * ⚠️ LE BANDEAU ET LE PIED DE PAGE SONT RÉSERVÉS ICI, et il a fallu une slide
 * tronquée en salle pour s'en apercevoir. `index.jsx` pose le titre de chapitre
 * en `absolute top-0` (64 px) et la navigation en `bottom-8 h-11` (76 px) : le
 * template reçoit une boîte qui passe DERRIÈRE les deux. La compensation du
 * haut existait (`pt-20` chez le parent), celle du bas non — et `p-12` ne
 * suffisait pas. Avec `justify-center`, un contenu trop haut déborde des DEUX
 * côtés à la fois : le titre passait sous « OUVERTURE » pendant que la légende
 * passait sous les flèches. D'où `pb-[76px]`, et ne pas le retirer.
 *
 * La figure est en `flex-1 min-h-0` plutôt qu'en hauteur fixe : sur un écran
 * court, les barres se resserrent au lieu de pousser la légende hors cadre.
 * Les colonnes, elles, sont `flex-shrink-0` — c'est le texte, il ne s'écrase
 * pas. Ne remets pas de `h-[Nvh]` sur la bande de barres.
 *
 * ⚠️ L'ÉCHELLE DES BARRES EST LINÉAIRE, et c'était une correction — ne pas la
 * repasser en logarithmique. En log, 6 pages occupait plus de la moitié de la
 * hauteur de 1 500 : l'échelle disait « c'est du même ordre » pendant que la
 * slide disait « ça n'a plus rien à voir ». Le graphique contredisait son
 * propre propos, et c'est le seul argument visuel de la slide.
 * En linéaire, 6 et 50 sont des traits à côté de 1 500 — c'est exactement ce
 * qu'on veut faire voir, et l'étiquette au-dessus de chaque barre donne la
 * valeur exacte à qui la cherche.
 */
const ComparaisonSlide = ({ slide, slideKey, currentSlide, slidesLength, currentPointIndex, typingStates }) => {
    const totalPoints = slide.left.points.length + slide.right.points.length;

    return (
        <div key={slideKey} className="h-full px-12 pt-3 pb-[76px] flex flex-col relative overflow-hidden">
            <div className="relative z-10 flex flex-col flex-1 min-h-0 justify-center">
                <h2
                    className="pb-2 font-bold mb-[1.5vh] bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-fade-in-up text-center"
                    style={{ fontSize: 'clamp(2rem, min(3.75rem, 7vh), 3.75rem)' }}
                >
                    {slide.title}
                </h2>
                <p className="text-2xl text-blue-200/60 mb-[3vh] leading-snug animate-fade-in-up stagger-1 text-center max-w-6xl mx-auto">
                    {slide.subtitle}
                </p>

                <div className="flex-shrink-0 grid grid-cols-2 gap-8 max-w-5xl mx-auto w-full">
                    {/* Colonne Gauche */}
                    <div className="space-y-[1.5vh]">
                        <div className="text-center mb-[2vh]">
                            <span className="inline-block px-6 py-[1.1vh] bg-red-500/20 border border-red-500/40 rounded-full text-red-300 font-bold text-2xl">
                                {slide.left.label}
                            </span>
                        </div>
                        {slide.left.points.map((point, i) => {
                            const globalIndex = i;
                            return (
                                <div key={i} className={`bg-red-500/5 border border-red-500/20 rounded-xl px-5 py-[1.6vh] hover:border-red-500/40 transition-all flex items-center ${globalIndex < currentPointIndex ? 'opacity-100 animate-fade-in-left' :
                                    globalIndex === currentPointIndex ? 'opacity-100 animate-fade-in-left' :
                                        'opacity-0'
                                    }`}>
                                    <p className="text-slate-300 font-mono text-xl leading-snug flex items-start gap-3 w-full">
                                        <span className="text-red-400 mt-1 flex-shrink-0">✗</span>
                                        <span className="flex-1">
                                            {globalIndex < currentPointIndex ? point :
                                                globalIndex === currentPointIndex ? (
                                                    <>
                                                        {typingStates[globalIndex] || ''}
                                                        {typingStates[globalIndex] !== point && <span className="typing-cursor"></span>}
                                                    </>
                                                ) : ''}
                                        </span>
                                    </p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Colonne Droite */}
                    <div className="space-y-[1.5vh]">
                        <div className="text-center mb-[2vh]">
                            <span className="inline-block px-6 py-[1.1vh] bg-green-500/20 border border-green-500/40 rounded-full text-green-300 font-bold text-2xl">
                                {slide.right.label}
                            </span>
                        </div>
                        {slide.right.points.map((point, i) => {
                            const globalIndex = i + slide.left.points.length;
                            return (
                                <div key={i} className={`bg-green-500/5 border border-green-500/20 rounded-xl px-5 py-[1.6vh] hover:border-green-500/40 transition-all flex items-center ${globalIndex < currentPointIndex ? 'opacity-100 animate-fade-in-right' :
                                    globalIndex === currentPointIndex ? 'opacity-100 animate-fade-in-right' :
                                        'opacity-0'
                                    }`}>
                                    <p className="text-slate-300 font-mono text-xl leading-snug flex items-start gap-3 w-full">
                                        <span className="text-green-400 mt-1 flex-shrink-0">✓</span>
                                        <span className="flex-1">
                                            {globalIndex < currentPointIndex ? point :
                                                globalIndex === currentPointIndex ? (
                                                    <>
                                                        {typingStates[globalIndex] || ''}
                                                        {typingStates[globalIndex] !== point && <span className="typing-cursor"></span>}
                                                    </>
                                                ) : ''}
                                        </span>
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {slide.figure && (
                    <FigureBande figure={slide.figure} visible={currentPointIndex >= totalPoints} />
                )}
            </div>
        </div>
    );
};

export default ComparaisonSlide;
